/**
 * LinkedContactPreview - Display rich contact details for linked contacts
 *
 * Used when showing a contact that will be linked via entity sync suggestions.
 * Fetches contact details by Filevine ID and displays them using ContactCard.
 */

import { AlertCircle, Loader2 } from 'lucide-react';
import React from 'react';

import { useFilevineContactByFilevineIdQuery } from '@/services/api/filevineResourcesService';

import { ContactCard, fromFilevineContactSchema } from './ContactCard';

// ============================================================================
// Types
// ============================================================================

interface LinkedContactPreviewProps {
  /** Filevine contact ID from suggestion payload */
  contactId: number;
  /** Firm ID for the API request */
  firmId: string;
}

// ============================================================================
// Loading & Fallback States
// ============================================================================

/** Loading state for the contact preview */
const ContactLoadingSkeleton: React.FC = () => (
  <span className="inline-flex items-center gap-1.5 text-zinc-500">
    <Loader2 className="h-3.5 w-3.5 animate-spin" />
    Loading contact...
  </span>
);

/** Fallback display when contact cannot be resolved */
const FallbackContactDisplay: React.FC<{ contactId: number }> = ({
  contactId,
}) => (
  <span className="inline-flex items-center gap-1.5 text-amber-700">
    <AlertCircle className="h-3.5 w-3.5" />
    Link to contact #{contactId}
  </span>
);

// ============================================================================
// Main Component
// ============================================================================

/**
 * LinkedContactPreview - Fetches and displays contact details by Filevine ID
 *
 * Used when showing a LINK_EXISTING_CONTACT suggestion to display
 * the actual contact that will be linked.
 */
export const LinkedContactPreview: React.FC<LinkedContactPreviewProps> = ({
  contactId,
  firmId,
}) => {
  const {
    data: contact,
    isLoading,
    error,
  } = useFilevineContactByFilevineIdQuery({
    firmId,
    filevineId: String(contactId),
  });

  if (isLoading) {
    return <ContactLoadingSkeleton />;
  }

  if (error || !contact) {
    return <FallbackContactDisplay contactId={contactId} />;
  }

  return (
    <ContactCard
      contact={fromFilevineContactSchema(contact)}
      variant="inline"
    />
  );
};

export default LinkedContactPreview;
